/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as SoundActionCreators from '@app/actions/SoundActionCreators';
import SoundStore from '@app/stores/SoundStore';
import * as CustomSoundDB from '@app/utils/CustomSoundDB';
import * as SoundUtils from '@app/utils/SoundUtils';
import {LimitResolver} from '@app/utils/limits/LimitResolverAdapter';
import {isLimitToggleEnabled} from '@app/utils/limits/LimitUtils';
import {SoundType} from '@app/utils/SoundUtils';

export interface EntranceSoundPlayResult {
	played: boolean;
	ended: Promise<void>;
}

const resolveEntranceSoundsEnabled = (): boolean =>
	isLimitToggleEnabled(
		{feature_voice_entrance_sounds: LimitResolver.resolve({key: 'feature_voice_entrance_sounds', fallback: 0})},
		'feature_voice_entrance_sounds',
	);

const fallbackEnded = (): Promise<void> => Promise.resolve();

export async function playEntranceSound(): Promise<EntranceSoundPlayResult> {
	if (!SoundStore.isSoundTypeEnabled(SoundType.UserJoin)) {
		return {played: false, ended: fallbackEnded()};
	}

	const hasEntranceSounds = resolveEntranceSoundsEnabled();

	if (!hasEntranceSounds) {
		await SoundActionCreators.playSound(SoundType.UserJoin);
		return {played: true, ended: fallbackEnded()};
	}

	try {
		const entranceSound = await CustomSoundDB.getEntranceSound();
		if (!entranceSound) {
			await SoundActionCreators.playSound(SoundType.UserJoin);
			return {played: true, ended: fallbackEnded()};
		}

		let handle: SoundUtils.SoundHandle | null = SoundUtils.playEntranceSoundBuffer();
		if (!handle) {
			const url = SoundUtils.getEntranceSoundBlobUrl();
			if (url) {
				const fallbackAudio = new Audio(url);
				const ended = new Promise<void>((resolve) => {
					fallbackAudio.onended = () => resolve();
					fallbackAudio.onerror = () => resolve();
				});
				try {
					await fallbackAudio.play();
					return {played: true, ended};
				} catch {
					await SoundActionCreators.playSound(SoundType.UserJoin);
					return {played: true, ended: fallbackEnded()};
				}
			}
			await SoundActionCreators.playSound(SoundType.UserJoin);
			return {played: true, ended: fallbackEnded()};
		}

		return {played: true, ended: handle.ended};
	} catch (e) {
		await SoundActionCreators.playSound(SoundType.UserJoin);
		return {played: true, ended: fallbackEnded()};
	}
}